import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS } from "../data";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Shows", id: "shows" },
  { label: "Gallery", id: "gallery" },
  { label: "Listen", id: "music" },
  { label: "Contact", id: "contact" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -10, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = ["home", "about", "shows", "gallery", "music", "contact"];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
        >
          <img
            src={ASSETS.wordmark}
            alt="TAO LUMIN"
            className="h-6 md:h-7 w-auto object-contain transition-opacity duration-300 group-hover:opacity-80"
            style={{ filter: "invert(1)" }}
          />
        </button>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => {
            const isActive = active === l.id;
            return (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`relative text-xs uppercase tracking-[0.2em] transition-colors duration-300 after:content-[''] after:absolute after:-bottom-2 after:left-0 after:h-px after:bg-[#003399] after:transition-all after:duration-300 ${
                  isActive
                    ? "text-[#F2F2F2] after:w-full"
                    : "text-[#F2F2F2]/70 hover:text-[#F2F2F2] after:w-0 hover:after:w-full"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          <button
            data-testid="nav-book-btn"
            onClick={() => go("contact")}
            className="px-5 py-2.5 bg-[#003399] text-[#F2F2F2] text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_28px_rgba(0,51,153,0.8)] transition-shadow duration-300"
          >
            Book
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#F2F2F2]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Scroll progress bar */}
      <motion.div
        data-testid="scroll-progress"
        style={{ scaleX }}
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#003399] origin-left"
      />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="font-display text-left text-xl uppercase text-[#F2F2F2]/80"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-btn"
                onClick={() => go("contact")}
                className="mt-2 px-5 py-3 bg-[#003399] text-[#F2F2F2] text-sm uppercase tracking-[0.2em]"
              >
                Book Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
